import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface CampaignMetric {
  id: string;
  name: string;
  status: string;
  sent: number;
  opened: number;
  openRate: number;
  clicked: number;
  clickRate: number;
  replied: number;
  replyRate: number;
  createdAt: Date;
}

interface ReportsCampaignTableProps {
  campaigns: CampaignMetric[];
  isLoading?: boolean;
}

export function ReportsCampaignTable({ campaigns, isLoading }: ReportsCampaignTableProps) {
  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Sent</TableHead>
            <TableHead className="text-right">Opened</TableHead>
            <TableHead className="text-right">Open %</TableHead>
            <TableHead className="text-right">Clicked</TableHead>
            <TableHead className="text-right">Click %</TableHead>
            <TableHead className="text-right">Replied</TableHead>
            <TableHead className="text-right">Reply %</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(3)].map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-4 w-32" /></TableCell>
              <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Card className="bg-card shadow-none border p-6 text-center text-foreground">
        No launched campaigns found
      </Card>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Campaign</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Sent</TableHead>
          <TableHead className="text-right">Clicked</TableHead>
          <TableHead className="text-right">Click %</TableHead>
          <TableHead className="text-right">Replied</TableHead>
          <TableHead className="text-right">Reply %</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {campaigns.map((campaign) => (
          <TableRow key={campaign.id}>
            <TableCell className="max-w-xs truncate">
              {campaign.name}
            </TableCell>
            <TableCell>
              <StatusBadge status={campaign.status} />
            </TableCell>
            <TableCell className="text-right">{campaign.sent}</TableCell>
            <TableCell className="text-right">{campaign.clicked}</TableCell>
            <TableCell className="text-right">
              {campaign.clickRate.toFixed(1)}%
            </TableCell>
            <TableCell className="text-right">{campaign.replied}</TableCell>
            <TableCell className="text-right">
              {campaign.replyRate.toFixed(1)}%
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const statusStyles: Record<string, string> = {
    draft: 'bg-status-draft-surface text-status-draft-foreground',
    active: 'bg-success-surface-strong text-success-foreground-deep',
    paused: 'bg-status-paused-surface text-status-paused-foreground',
    completed: 'bg-info-surface-strong text-info-foreground-deep',
  };

  const styles = statusStyles[status] || 'bg-surface-subtle text-content-body-strong';

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${styles}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
